import { Canvas, useThree, CanvasProps } from '@react-three/fiber';
import React, { FC, memo, ReactNode, useEffect, useRef, useState } from 'react';

type Props = {
  children: ReactNode;
  canvasProps?: CanvasProps;
};

const toEven = (v: number) => (v % 2 === 0 ? v : v - 1);

const getViewportSize = () => ({
  width: toEven(Math.floor(window.innerWidth)),
  height: toEven(Math.floor(window.innerHeight)),
});

const RESIZE_DEBOUNCE_MS = 1000;

const CanvasWrapper: FC<Props> = memo(({ children, canvasProps }: Props) => {
  const [size, setSize] = useState(getViewportSize);
  const resizeTimeout = useRef<number | null>(null);

  useEffect(() => {
    const onResize = () => {
      if (resizeTimeout.current != null) {
        clearTimeout(resizeTimeout.current);
      }

      resizeTimeout.current = window.setTimeout(() => {
        const { width, height } = getViewportSize();

        setSize((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);

      if (resizeTimeout.current != null) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  return (
    <div style={{}}>
      <Canvas style={{ width: size.width, height: size.height }} {...canvasProps}>
        <InvalidateOnResize />
        {children}
      </Canvas>
    </div>
  );
});

CanvasWrapper.displayName = 'CanvasWrapper';

export { CanvasWrapper };

const InvalidateOnResize = () => {
  const { invalidate } = useThree();
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    const handle = () => {
      if (timeout.current != null) {
        clearTimeout(timeout.current);
      }

      timeout.current = window.setTimeout(() => {
        invalidate();
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handle);

    return () => {
      window.removeEventListener('resize', handle);

      if (timeout.current != null) {
        clearTimeout(timeout.current);
      }
    };
  }, [invalidate]);

  return null;
};
